import { useCallback, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import type {
  ContactMetier,
  EnrichedContactMetier,
  Territoire,
  PerimetreWithoutTerritoire,
  PerimetreAvecCommunesOuCads,
  Poste,
} from "models";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import { contactsMetiersService } from "services/contactsMetiersService";
import { arrayUtil } from "@sdeapps/react-core";
import { perimetresService } from "services";
import type { ISearchData } from "./ISearchData";
import { SearchContext } from "./SearchContext";
import type { SearchFilterFunction, SearchFilterFunctions, SearchFilters } from "./SearchFilters";
import { emptyFilters } from "./SearchFilters";
import { contactsMetiersUtil, sortingUtil } from "utils";
import type {
  SearchSortFunction,
  SearchTableSortableColumnKey,
  TableColumnHead,
} from "./SearchSorting";
import { SortingOrder } from "./SearchSorting";
import { enqueueSnackbar } from "notistack";
import { ToastMessages } from "enums";

function enrichContactMetier(
  cm: ContactMetier,
  perimetresAvecCommunes: Array<PerimetreAvecCommunesOuCads>
): EnrichedContactMetier {
  let _perimetres: Array<PerimetreAvecCommunesOuCads> = [];
  let _territoires: Array<Territoire> = [];

  if (contactsMetiersUtil.isCommune(cm)) {
    _perimetres = perimetresAvecCommunes
      .filter((p) => p.competence === (cm.metier.competence ?? p.competence))
      .filter((p) => p.communes.map(({ id }) => id).includes(cm.commune.id));
    _perimetres.sort(sortingUtil.sortPerimetres);

    _territoires = arrayUtil.dedupArray(
      _perimetres.map((p) => p.territoire),
      (t) => t.id
    );
    _territoires.sort(sortingUtil.sortTerritoires);
  } else if (contactsMetiersUtil.isCommuneAssocieeDeleguee(cm)) {
    _perimetres = perimetresAvecCommunes
      .filter((p) => p.competence === (cm.metier.competence ?? p.competence))
      .filter((p) => p.communesAssocieesDeleguees?.map(({ id }) => id).includes(cm.cad.id));
    _perimetres.sort(sortingUtil.sortPerimetres);

    _territoires = arrayUtil.dedupArray(
      _perimetres.map((p) => p.territoire),
      (t) => t.id
    );
    _territoires.sort(sortingUtil.sortTerritoires);
  } else if (contactsMetiersUtil.isPerimetre(cm)) {
    const p = perimetresAvecCommunes.find((p) => p.id === cm.perimetre?.id);
    if (p != null) _perimetres = [p];
    if (p?.territoire != null) _territoires = [p.territoire];
  } else if (contactsMetiersUtil.isTerritoire(cm)) {
    _perimetres = [];
    _territoires = [cm.territoire];
  }

  return {
    ...cm,
    perimetres: _perimetres.map(({ id, libelle, competence, code }) => ({
      id,
      libelle,
      code,
      competence,
    })),
    territoires: _territoires,
  };
}

const filterFunctions: SearchFilterFunctions = {
  communesOuCommunesAssocieesDeleguees: (cm, filtersArray) => {
    if (contactsMetiersUtil.isCommune(cm)) {
      return filtersArray
        .filter((c) => c._type === "commune")
        .map((c) => c.id)
        .includes(cm.commune.id);
    } else if (contactsMetiersUtil.isCommuneAssocieeDeleguee(cm)) {
      return filtersArray
        .filter((c) => c._type === "commune-associee-deleguee")
        .map((c) => c.id)
        .includes(cm.cad.id);
    } else {
      return false;
    }
  },
  competences: (cm, filtersArray) =>
    cm.metier?.competence != null && filtersArray.includes(cm.metier.competence),
  metiers: (cm, filtersArray) => filtersArray.map((m) => m.code).includes(cm.metier.code),
  perimetres: (cm, filtersArray) =>
    arrayUtil.isCommonElements(
      filtersArray.map(({ id }) => id),
      cm.perimetres?.map(({ id }) => id) ?? []
    ),
  postes: (cm, filtersArray) => filtersArray.map((p) => p.codeAstre).includes(cm.poste.codeAstre),
  isPostesVacants: (cm, isVacant) => isVacant == null || cm.poste.isVacant === isVacant,
  territoires: (cm, filtersArray) =>
    arrayUtil.isCommonElements(
      filtersArray.map((t) => t.id),
      cm.territoires?.map(({ id }) => id) ?? []
    ),
};

function getTokenPerimetre(cm: EnrichedContactMetier): PerimetreWithoutTerritoire | undefined {
  if (cm.perimetres == null) return undefined;
  return [...cm.perimetres]?.sort(sortingUtil.sortPerimetres)?.[0];
}

function getTokenTerritoire(cm: EnrichedContactMetier): Territoire | undefined {
  if (cm.territoires == null) return undefined;
  return [...cm.territoires]?.sort(sortingUtil.sortTerritoires)?.[0];
}

const sortableTableHeads: Array<TableColumnHead> = [
  {
    id: "territoire",
    libelle: "Territoire",
    sortFunction: (cm1, cm2) => {
      const cm1TokenTerritoire = getTokenTerritoire(cm1);
      const cm2TokenTerritoire = getTokenTerritoire(cm2);
      if (cm1TokenTerritoire == null) return 1;
      if (cm2TokenTerritoire == null) return -1;
      return sortingUtil.sortTerritoires(cm1TokenTerritoire, cm2TokenTerritoire);
    },
  },
  {
    id: "perimetre",
    libelle: "Périmètre",
    sortFunction: (cm1, cm2) => {
      const cm1TokenPerimetre = getTokenPerimetre(cm1);
      const cm2TokenPerimetre = getTokenPerimetre(cm2);
      if (cm1TokenPerimetre == null) return 1;
      if (cm2TokenPerimetre == null) return -1;
      return sortingUtil.sortPerimetres(cm1TokenPerimetre, cm2TokenPerimetre);
    },
  },
  {
    id: "commune",
    libelle: "Commune",
    sortFunction: (cm1, cm2) => {
      if (!contactsMetiersUtil.isLikeACommune(cm1)) {
        return 1;
      }
      if (!contactsMetiersUtil.isLikeACommune(cm2)) {
        return -1;
      }

      const cm1TokenCommuneOrCad = contactsMetiersUtil.isCommune(cm1) ? cm1.commune : cm1.cad;
      const cm2TokenCommuneOrCad = contactsMetiersUtil.isCommune(cm2) ? cm2.commune : cm2.cad;
      return sortingUtil.sortCommunes(cm1TokenCommuneOrCad, cm2TokenCommuneOrCad);
    },
  },
  {
    id: "metier",
    libelle: "Métier",
    sortFunction: (cm1, cm2) => sortingUtil.sortMetiers(cm1.metier, cm2.metier),
  },
  {
    id: "poste",
    libelle: "Poste",
    sortFunction: (cm1, cm2) => sortingUtil.sortPostes(cm1.poste, cm2.poste),
  },
];

function getInverseOrder(order: SortingOrder): SortingOrder {
  return order === SortingOrder.ASC ? SortingOrder.DESC : SortingOrder.ASC;
}

interface SearchProviderProps extends PropsWithChildren {}

export function SearchProvider({ children }: Readonly<SearchProviderProps>): ReactNode {
  const [baseContactsMetiers, setBaseContactsMetiers] = useState<Array<ContactMetier>>([]);
  const [perimetresAvecCommunes, setPerimetresAvecCommunes] = useState<
    Array<PerimetreAvecCommunesOuCads>
  >([]);

  const [filters, setFilters] = useState<SearchFilters>(emptyFilters);
  const [order, setOrder] = useState<SortingOrder>(SortingOrder.ASC);
  const [orderBy, setOrderBy] = useState<SearchTableSortableColumnKey>();
  const [sortFunction, setSortFunction] = useState<SearchSortFunction>();

  // #region Getting data
  const { catchErrors, isLoading: isContactsMetiersLoading } = useSnackbarErrorHandler();
  const { catchErrors: catchPerimetresErrors, isLoading: isPerimetresLoading } =
    useSnackbarErrorHandler({
      default: () => {
        enqueueSnackbar({ variant: "error", message: ToastMessages.ERROR_PERIMETRES_FETCH });
      },
    });

  const updateAllContactsMetiers = useCallback(async (): Promise<void> => {
    async function getPerimetres(): Promise<void> {
      const [perimetresAvecCommunes, perimetresSansCommunes] = await Promise.all([
        perimetresService.getPerimetresAvecCommunes(),
        perimetresService.getAll(),
      ]);

      // Il manque aux perimetresAvecCommunes les périmètres qui ne sont pas retournés par cet endpoint,
      // ceux sans communes, comme les périmètres du Bas-Rhin par exemple.
      // On les trouve depuis le getAll (perimetresSansCommunes) et on les ajoute.
      const newPerimetresList = perimetresAvecCommunes.concat(
        perimetresSansCommunes
          .filter((p) => !perimetresAvecCommunes.some((_p) => _p.id === p.id))
          .map((p) => ({
            ...p,
            communes: [],
            communesAssocieesDeleguees: [],
          }))
      );

      setPerimetresAvecCommunes(newPerimetresList);

      if (perimetresAvecCommunes.length === 0 || perimetresSansCommunes.length === 0) {
        throw new Error("Aucun périmètre n'a pu être récupéré.");
      }
    }

    async function getRecursivelyAllContactsMetiers(
      _contactsMetiers: Array<ContactMetier> = [],
      continuationToken?: string
    ): Promise<Array<ContactMetier>> {
      const _pageableContactsMetiers = await contactsMetiersService.getAll(continuationToken);
      const newContactsMetiers = _contactsMetiers.concat(_pageableContactsMetiers.values);
      setBaseContactsMetiers(newContactsMetiers);

      if (_pageableContactsMetiers.continuationToken != null) {
        return await getRecursivelyAllContactsMetiers(
          newContactsMetiers,
          _pageableContactsMetiers.continuationToken
        );
      }
      return newContactsMetiers;
    }

    async function getAllContactsMetiers(): Promise<void> {
      setBaseContactsMetiers([]);
      const _contactsMetiers = await getRecursivelyAllContactsMetiers();
      setBaseContactsMetiers(_contactsMetiers);
    }

    void catchPerimetresErrors(getPerimetres);
    await catchErrors(getAllContactsMetiers);
  }, [catchErrors, catchPerimetresErrors]);

  // Enriching contacts metiers data with perimetres and territoires
  const enrichedContactsMetiers = useMemo(() => {
    if (perimetresAvecCommunes.length === 0) {
      return baseContactsMetiers;
    } else {
      const _enrichedContactsMetiers: Array<EnrichedContactMetier> = [...baseContactsMetiers].map(
        (cm) => enrichContactMetier(cm, perimetresAvecCommunes)
      );
      return _enrichedContactsMetiers;
    }
  }, [baseContactsMetiers, perimetresAvecCommunes]);

  useEffect(() => {
    void updateAllContactsMetiers();
  }, [updateAllContactsMetiers]);
  // #endregion

  // #region Modification and Deletion
  const deleteContactMetier = useCallback(
    (deletedContactMetier: ContactMetier) => {
      setBaseContactsMetiers(
        [...baseContactsMetiers].filter((cm) => cm.id !== deletedContactMetier.id)
      );
    },
    [baseContactsMetiers]
  );

  const modifyContactMetier = useCallback(
    (editedContactMetier: ContactMetier) => {
      setBaseContactsMetiers(
        baseContactsMetiers.map((cm) =>
          cm.id === editedContactMetier.id ? editedContactMetier : cm
        )
      );
    },
    [baseContactsMetiers]
  );

  const modifyContactsMetiers = useCallback(
    (contactsMetiersIdsToUpdate: Array<string>, nouveauPoste: Poste): void => {
      setBaseContactsMetiers(
        baseContactsMetiers.map((cm) =>
          contactsMetiersIdsToUpdate.includes(cm.id) ? { ...cm, poste: nouveauPoste } : cm
        )
      );
    },
    [baseContactsMetiers]
  );
  // #endregion

  // #region Filtering and sorting
  const filteredContactsMetiers = useMemo(() => {
    let _contactsMetiers = [...enrichedContactsMetiers];
    for (const [key, filterFunction] of Object.entries(filterFunctions) as Array<
      [keyof SearchFilterFunctions, SearchFilterFunction<keyof SearchFilterFunctions>]
    >) {
      if (!Array.isArray(filters[key]) || filters[key].length > 0) {
        _contactsMetiers = _contactsMetiers.filter((cm) => filterFunction(cm, filters[key]));
      }
    }

    if (orderBy != null && sortFunction != null) {
      return _contactsMetiers.toSorted(
        (a, b) => sortFunction(a, b) * (order === SortingOrder.ASC ? 1 : -1)
      );
    }
    return _contactsMetiers;
  }, [enrichedContactsMetiers, filters, order, orderBy, sortFunction]);

  const resetAllFilters = useCallback(() => {
    setFilters(emptyFilters);
  }, []);

  const setSortColumn = useCallback(
    (column: TableColumnHead) => {
      if (column.id === orderBy) {
        setOrder(getInverseOrder(order));
      } else {
        setOrder(SortingOrder.ASC);
      }
      setOrderBy(column.id);

      setSortFunction(() => column.sortFunction);
    },
    [order, orderBy]
  );
  // #endregion

  const data: ISearchData = useMemo(
    () => ({
      filteredContactsMetiers,
      deleteContactMetier,
      modifyContactMetier,
      modifyContactsMetiers,
      updateAllContactsMetiers,
      isLoading: isContactsMetiersLoading || isPerimetresLoading,
      isContactsMetiersLoading,
      isPerimetresLoading,

      filters,
      setFilters,
      resetAllFilters,

      sortableTableHeads,
      orderBy,
      order,
      sortFunction,
      setSortColumn,
    }),
    [
      filteredContactsMetiers,
      deleteContactMetier,
      modifyContactMetier,
      modifyContactsMetiers,
      updateAllContactsMetiers,
      isContactsMetiersLoading,
      isPerimetresLoading,
      filters,
      resetAllFilters,
      orderBy,
      order,
      sortFunction,
      setSortColumn,
    ]
  );

  return <SearchContext.Provider value={data}>{children}</SearchContext.Provider>;
}
